import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "@tsp-core/store/utils";

import { CountryUuid } from "@tsp-domains/countries/types";

import {
  AttachPartyToNewCompanyPayload,
  CompaniesDictionary,
  CompaniesDictionaryRecord,
  CompaniesDictionaryRecords,
  CompanyUuid,
  SearchCompaniesPayload,
  UpdatePartyCompanyDataPayload,
} from "../types";

type GetCompaniesRequestParams = {
  name: string;
  countryUuid: CountryUuid;
  vatNumber: string;
};

type GetCompaniesResponseData = {
  items: CompaniesDictionaryRecords;
};

type GetCompanyRequestParams = {
  uuid: CompanyUuid;
};

type GetCompanyResponseData = {
  uuid: CompanyUuid;
  name: string;
  countryUuid: CountryUuid;
  vatNumber: string;
  vat: number;
};

type AttachPartyToExistCompanyRequestParams = {
  companyUuid: CompanyUuid;
};

type AttachPartyToNewCompanyRequestParams = {
  companyName: string;
  countryUuid: CountryUuid;
  vatNumber: string;
};

type UpdatePartyCompanyDataRequestParams = {
  name: string;
  countryUuid: CountryUuid;
  vatNumber: string;
};

export const companiesApi = createApi({
  reducerPath: "@companies/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  keepUnusedDataFor: 0,
  tagTypes: ["CompaniesDictionary", "CompaniesDictionaryRecord"],
  endpoints: (builder) => ({
    searchCompanies: builder.query<CompaniesDictionary, SearchCompaniesPayload>({
      query: (payload) => {
        const body: GetCompaniesRequestParams = {
          name: payload.companyName,
          countryUuid: payload.countryUuid,
          vatNumber: payload.vatNumber ? payload.vatNumber.toUpperCase() : null,
        };

        return {
          url: "/party/companies",
          method: "POST",
          body,
        };
      },
      transformResponse: (response: GetCompaniesResponseData): CompaniesDictionary => {
        return response.items.reduce((accumulator, item) => {
          accumulator[item.uuid] = item;
          return accumulator;
        }, {});
      },
      providesTags: ["CompaniesDictionary"],
    }),
    getCompany: builder.query<CompaniesDictionaryRecord, CompanyUuid>({
      query: (uuid) => {
        const body: GetCompanyRequestParams = {
          uuid,
        };

        return {
          url: "/party/getCompany",
          method: "POST",
          body,
        };
      },
      transformResponse: (response: GetCompanyResponseData): CompaniesDictionaryRecord => {
        return {
          uuid: response.uuid,
          name: response.name,
          countryUuid: response.countryUuid,
          vatNumber: response.vatNumber || "",
          vat: response.vat,
        };
      },
      providesTags: ["CompaniesDictionaryRecord"],
    }),
    attachPartyToExistCompany: builder.mutation<void, CompanyUuid>({
      query: (uuid) => {
        const body: AttachPartyToExistCompanyRequestParams = {
          companyUuid: uuid,
        };

        return {
          url: "/party/company/attachToExisting",
          method: "POST",
          body,
        };
      },
    }),
    attachPartyToNewCompany: builder.mutation<void, AttachPartyToNewCompanyPayload>({
      query: (payload) => {
        const body: AttachPartyToNewCompanyRequestParams = {
          companyName: payload.companyName,
          countryUuid: payload.countryUuid,
          vatNumber: payload.vatNumber ? payload.vatNumber.toUpperCase() : null,
        };

        return {
          url: "/party/company/attachToNew",
          method: "POST",
          body,
        };
      },
    }),
    updatePartyCompanyData: builder.mutation<void, UpdatePartyCompanyDataPayload>({
      query: (payload) => {
        const body: UpdatePartyCompanyDataRequestParams = {
          name: payload.companyName,
          countryUuid: payload.countryUuid,
          vatNumber: payload.vatNumber ? payload.vatNumber.toUpperCase() : null,
        };

        return {
          url: "/party/updateCompanyData",
          method: "POST",
          body,
        };
      },
    }),
    removePartyAttachmentCompany: builder.mutation<void, void>({
      query: () => {
        return {
          url: "/party/company/removeCompanyAttachment",
          method: "POST",
        };
      },
    }),
  }),
});
